import { defaultValueRequest } from "components/widgets/charts/Sparkline/SparklineSettings";
import { Query } from "system/query/Query";
import { AutoWidgetQueryBehavior, Widget, WidgetQuery, WidgetQueryBehavior, WidgetRequest, WidgetRequestInfo, WidgetTemplateQueryBehavior } from "system/widget/Widget";

export function isOldStyleWidgetRequest(widgetRequestInfo: any): boolean {
  if (!widgetRequestInfo || typeof widgetRequestInfo !== "object") {
    return false;
  }
  // If there are any of these keys present, it's an old style query request.
  const oldRequestKeys = ["body", "query_options", "aggs", "topics", "clusters", "histogram", "single", "multi", "query_id", "showMultiTemplateQueries"];
  const foundKeys = oldRequestKeys.some(key => Object.prototype.hasOwnProperty.call(widgetRequestInfo, key));
  return !!foundKeys;
}

interface OldWidgetSettings {
  name: string;
  endpoint: string;
  query_options?: {
    flatten?: boolean;
    include_total?: boolean;
  };
  autoWidgetQueryBehavior?: AutoWidgetQueryBehavior,
  widgetQueryBehavior?: WidgetQueryBehavior,
  widgetTemplateQueryBehavior?: WidgetTemplateQueryBehavior,

}

const OldWidgetSettingsByType: Record<string, OldWidgetSettings> = {
  "ai-social-personas-structured": {
    name: 'ai-summary',
    endpoint: 'query/test',
    query_options: { }
  },
  "ai-summary-structured": {
    name: 'ai-summary',
    endpoint: 'query/test',
    query_options: { }
  },
  "arc-chart": {
    name: 'arc-chart',
    endpoint: 'query/agg',
    query_options: {
      flatten: true,
      include_total: true
    }
  },
  "bar-chart": {
    name: 'bar-chart',
    endpoint: 'query/agg',
    query_options: {
      flatten: true,
    }
  },
  "category-arcs": {
    name: 'category-arcs',
    endpoint: 'query/agg',
    query_options: {
      include_total: true
    },
    autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_MULTI_WHEN_ALL,
  },
  "category-bars": {
    name: 'category-bars',
    endpoint: 'query/agg',
    query_options: {
      include_total: true
    },
    autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_MULTI_WHEN_ALL,
  },
  "chord-chart": {
    name: 'chord-chart',
    endpoint: 'query/agg',
    query_options: {
      include_total: true
    }
  },
  "choropleth": {
    name: 'choropleth',
    endpoint: 'query/agg',
    query_options: { }
  },
  "data-table": {
    name: 'data-table',
    endpoint: 'query/agg',
    query_options: { }
  },
  "embed-list": {
    name: 'embed-list',
    endpoint: 'query/records',
    query_options: { }
  },
  "force-graph": {
    name: 'force-graph',
    endpoint: 'query/cluster-graph',
    query_options: { }
  },
  "heatmap": {
    name: 'heatmap',
    endpoint: 'query/records',
    query_options: {
      flatten: true
    }
  },
  "mentions-over-time": {
    name: 'mentions-over-time',
    endpoint: 'query/agg',
    query_options: {
      flatten: true,
    }
  },
  "narrative-table": {
    name: 'narrative-table',
    endpoint: 'query/cluster-graph',
    query_options: { }
  },
  "net-value": {
    name: 'net-value',
    endpoint: 'query/agg',
    query_options: {
      include_total: true
    }
  },
  "net-value-trend": {
    name: 'net-value-trend',
    endpoint: 'query/agg',
    query_options: {
      flatten: true,
      include_total: true
    }
  },
  "numeric-card": {
    name: 'numeric-card',
    endpoint: 'query/agg',
    query_options: {
      flatten: true,
      include_total: true
    }
  },
  "percent-change": {
    name: 'percent-change',
    endpoint: 'query/agg',
    query_options: {
      flatten: true,
      include_total: true
    }
  },
  "pie-chart": {
    name: 'pie-chart',
    endpoint: 'query/agg',
    query_options: {
      flatten: true
    }
  },
  "post-list": {
    name: 'post-list',
    endpoint: 'query/records',
    query_options: { }
  },
  "radar-chart": {
    name: 'radar-chart',
    endpoint: 'query/agg',
    query_options: { }
  },
  "radial-sentiment": {
    name: 'radial-sentiment',
    endpoint: 'query/agg',
    query_options: { }
  },
  "record-table": {
    name: 'record-table',
    endpoint: 'query/records',
    query_options: { }
  },
  "single-bar-chart": {
    name: "single-bar-chart",
    endpoint: 'query/agg',
    query_options: {
      flatten: false,
      include_total: true
    }
  },
  "social-influencers": {
    name: 'social-influencers',
    endpoint: 'query/social-influencers',
    query_options: { }
  },
  "sparkline": {
    name: 'sparkline',
    endpoint: 'query/agg',
    query_options: {
      flatten: true,
    }
  },
  "topic-table": {
    name: 'topic-table',
    endpoint: 'query/topics',
    query_options: { }
  },
  "topics-tree-map": {
    name: 'topics-tree-map',
    endpoint: 'query/topics',
    query_options: { }
  },
  "trend-chart": {
    name: 'trend-chart',
    endpoint: 'query/agg',
    query_options: {
      flatten: true,
    },
    autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_MULTI_WHEN_ALL,
  },
  "word-cloud": {
    name: 'word-cloud',
    endpoint: 'query/topics',
    query_options: { }
  }
};

export function convertOldStyleWidgetRequest(widget: Widget, widgetQueries: Query[]): WidgetRequestInfo | undefined {
  const oldRequest = {...widget.request_info};
  const oldWidgetSettings = OldWidgetSettingsByType[widget.name] || {}
  if (!oldWidgetSettings) {
    return undefined;
  }

  const queryParams = {
    ...(oldWidgetSettings.query_options || {}),
    ...(oldRequest.query_options || {})
  };
  delete oldRequest.query_options;

  let oldRequestBody = oldRequest;
  if (oldRequest?.body) {
    oldRequestBody = oldRequest.body;
  }
  delete oldRequest.body;

  let singleRequestInit = {...oldRequest, ...oldRequestBody};
  let multiRequestInit = undefined;

  if (oldRequestBody?.single) {
    singleRequestInit = {...oldRequest, ...oldRequestBody.single};
  }

  if (oldRequestBody?.multi) {
    multiRequestInit = {...oldRequest, ...oldRequestBody.multi};
  }

  const queries:WidgetQuery[] = [];
  if (oldRequest.query_id) {
    const query = widgetQueries.find(q => q.id === oldRequest.query_id);
    if (query) {
      queries.push({
        id: query.id,
        name: query.name || "unknown",
      });
    }
  }

  let autoWidgetQueryBehavior:AutoWidgetQueryBehavior =  AutoWidgetQueryBehavior.AUTO_SINGLE;
  if ((oldWidgetSettings.widgetTemplateQueryBehavior  && multiRequestInit) || oldWidgetSettings.autoWidgetQueryBehavior) {
    autoWidgetQueryBehavior = AutoWidgetQueryBehavior.AUTO_MULTI_WHEN_ALL;
  }
  const widgetRequest = {
    name: oldWidgetSettings.name || widget.name,
    endpoint: oldWidgetSettings.endpoint,
    requestInit: {
      body: singleRequestInit || '{}',
    },
    multiRequestInit: multiRequestInit ? { body: multiRequestInit } : undefined,

    autoWidgetQueryBehavior: autoWidgetQueryBehavior,

    queryParams: queryParams
  } as WidgetRequest;

  const requestInfo = {
    queries: queries,
    requests: [ widgetRequest ]
  } as WidgetRequestInfo;

  // Special case for mentions-over-time to add the ranges request
  // Handled this way since this is the only widget that needs a second request
  if (widget.name === "mentions-over-time") {
    requestInfo.requests.push({
      name: 'mentions-over-time-ranges',
      endpoint: 'query/ranges',
      requestInit: {},
      queryParams: {
        field: 'published'
      },
      autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
    } as WidgetRequest);
  }
  // Special case for sparkline to handle the second request used to display the value shown above 
  // the sparkline.
  else if (widget.name === "sparkline") {
    // We need to add the requestInit from the original query to the secondary query
    // to ensure we are getting the correct value from exisiting widgets for the sparkline-value request
    const localAggs = requestInfo.requests[0]?.requestInit?.body?.aggs?.histogram?.aggs || {};
      defaultValueRequest.requestInit = {
        body: {
          aggs: {
            ...localAggs,
          }
        }
      };
    requestInfo.requests.push(defaultValueRequest);
  }

  widget.request_info = requestInfo;
  return requestInfo;
}
